<template>
  <Head title="AI Chat" />

  <!-- Loading state while getting fingerprint -->
  <div v-if="isInitializing" class="flex items-center justify-center h-full w-full bg-white">
    <div class="text-center">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
      <p class="text-sm text-gray-600">Загрузка...</p>
    </div>
  </div>

  <div v-else class="flex flex-col h-full w-full bg-white overflow-hidden">
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0 w-full">
      <div class="flex items-center gap-2 min-w-0">
        <div class="w-2 h-2 bg-violet-600 rounded-full flex-shrink-0"></div>
        <span class="text-sm font-medium text-gray-900">AIAcademy</span>
      </div>
      <button
        @click="clearChat"
        class="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Новый чат
      </button>
    </div>

    <!-- Messages Area -->
    <div class="flex-1 overflow-y-auto overflow-x-hidden w-full min-h-0" ref="messagesContainer" @click="trackRegistrationClick">
      <div class="max-w-3xl mx-auto px-4 py-6">
        <!-- Welcome Message -->
        <div v-if="messages.length === 0" class="text-center py-12">
          <div class="mb-4">
            <svg class="w-12 h-12 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
          <h2 class="text-2xl font-semibold text-gray-900 mb-2">Как я могу вам помочь?</h2>
          <p class="text-gray-500 text-sm">Для начала разговора напишите сообщение ниже</p>
        </div>

        <!-- Messages -->
        <div v-for="(message, index) in messages" :key="index" class="mb-6">
          <ChatMessage :message="message" />
        </div>

        <!-- Loading Indicator -->
        <div v-if="isLoading" class="mb-6">
          <div class="flex gap-3">
            <div class="flex-shrink-0">
              <div class="w-8 h-8 rounded-full flex items-center justify-center p-1">
                <img :src="aiLogo" alt="AI" class="w-full h-full" />
              </div>
            </div>
            <div class="flex-1">
              <div class="flex gap-1 py-3">
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Input Area -->
    <div class="border-t border-gray-200 bg-white flex-shrink-0 w-full">
      <div class="max-w-3xl mx-auto px-4 py-4">
        <form @submit.prevent="sendMessage" class="flex items-center gap-2">
          <!-- Attachment buttons on the left -->
          <div class="flex gap-1 flex-shrink-0">
            <button
              type="button"
              @click="showRegistrationModal = true"
              class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Прикрепить изображение"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              type="button"
              @click="showRegistrationModal = true"
              class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Прикрепить файл"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>
          </div>

          <!-- Text input with send button -->
          <div class="relative flex-1">
            <textarea
              v-model="inputMessage"
              @keydown.enter.exact.prevent="sendMessage"
              placeholder="Введите сообщение..."
              rows="1"
              class="w-full px-4 py-3 pr-12 text-gray-900 bg-gray-100 border-0 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-gray-300 max-h-48 overflow-y-auto block"
              style="word-break: break-word;"
              ref="textareaRef"
            ></textarea>
            <button
              type="submit"
              :disabled="!inputMessage.trim() || isLoading"
              class="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              :class="inputMessage.trim() && !isLoading ? 'bg-gray-900 hover:bg-gray-800' : 'bg-gray-400'"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Registration Modal -->
    <div v-if="showRegistrationModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click="showRegistrationModal = false">
      <div class="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl" @click.stop>
        <div class="flex items-center justify-center mb-4">
          <div class="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
            <svg class="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </div>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 text-center mb-2">Прикрепление файлов</h3>
        <p class="text-gray-600 text-center mb-6">
          Возможность прикреплять изображения и файлы доступна только для зарегистрированных пользователей
        </p>
        <div class="flex gap-3">
          <button
            @click="showRegistrationModal = false"
            class="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Отмена
          </button>
          <a
            href="https://chat.aiacademy.me/register"
            target="_blank"
            rel="noopener noreferrer"
            @click="trackEvent('widget_register_click')"
            class="flex-1 px-4 py-2 text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors text-center"
          >
            Зарегистрироваться
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { Head } from '@inertiajs/vue3'
import ChatMessage from './ChatMessage.vue'
import { getUserFingerprint } from '../../lib/fingerprint.js'
import { createConsumer } from '@rails/actioncable'
import aiLogo from '/assets/ai_academy_logo.svg'

// Props from the server
const props = defineProps({
  ai_model: {
    type: String,
    default: 'Claude Sonnet 4.6'
  },
  ai_provider: {
    type: String,
    default: 'Anthropic'
  },
  limit_message: {
    type: String,
    default: ''
  },
  prompt: {
    type: String,
    default: null
  }
})

const trackEvent = (event) => {
  window.parent.postMessage({type: 'aiacademy_widget_event', event, widget_type: 'text'}, 'https://aiacademy.me')
}

const messages = ref([])
const inputMessage = ref(props.prompt || '')
const isLoading = ref(false)
const messagesContainer = ref(null)
const textareaRef = ref(null)
const tempChatId = ref(null)
const userId = ref(null)
const isInitializing = ref(true)
const cable = ref(null)
const subscription = ref(null)
const typingIntervalId = ref(null)
const showRegistrationModal = ref(false)

// Auto-resize textarea
watch(inputMessage, () => {
  nextTick(() => {
    if (textareaRef.value) {
      textareaRef.value.style.height = 'auto'
      textareaRef.value.style.height = textareaRef.value.scrollHeight + 'px'
    }
  })
})

// Scroll to bottom when new messages arrive
watch(messages, () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}, { deep: true })

// Subscribe/unsubscribe to ActionCable when chat is opened/closed
watch(tempChatId, (newTempChatId, oldTempChatId) => {
  // Unsubscribe from old channel if exists
  if (oldTempChatId && subscription.value) {
    console.log('Unsubscribing from widget_chat_' + oldTempChatId)
    subscription.value.unsubscribe()
    subscription.value = null
  }

  // Subscribe to new channel if chat is open
  if (newTempChatId) {
    console.log('Subscribing to widget_chat_' + newTempChatId)
    subscribeToChat(newTempChatId)
  }
})

// Typing animation function
const animateTyping = (fullText, reasoning = '') => {
  // Clear any existing typing animation
  if (typingIntervalId.value) {
    clearInterval(typingIntervalId.value)
    typingIntervalId.value = null
  }

  // Hide loading indicator immediately
  isLoading.value = false

  // Create message with empty content initially
  const messageIndex = messages.value.length
  const newMessage = {
    role: 'assistant',
    content: '',
    reasoning,
    timestamp: new Date()
  }
  messages.value.push(newMessage)

  let currentIndex = 0
  const charsPerInterval = 5
  const intervalMs = 30 // 50 chars/sec = 1000ms / 50 = 20ms per char

  typingIntervalId.value = setInterval(() => {
    if (currentIndex < fullText.length) {
      // Add next character(s)
      currentIndex += charsPerInterval
      messages.value[messageIndex].content = fullText.substring(0, currentIndex)
    } else {
      // Animation complete
      clearInterval(typingIntervalId.value)
      typingIntervalId.value = null
    }
  }, intervalMs)
}

const subscribeToChat = (chatId) => {
  if (!cable.value) {
    cable.value = createConsumer()
  }

  subscription.value = cable.value.subscriptions.create(
    {
      channel: 'WidgetChatChannel',
      temp_chat_id: chatId,
      fingerprint: userId.value
    },
    {
      connected() {
        console.log('Connected to WidgetChatChannel for chat ' + chatId)
      },
      disconnected() {
        console.log('Disconnected from WidgetChatChannel')
      },
      received(data) {
        console.log('Received message from ActionCable:', data)

        // Handle incoming AI message
        if (data.type === 'new_message' && data.message) {
          if (!data.message.error) trackEvent('widget_success')
          const fullText = data.message.content || data.message.text
          const reasoning = data.message.reasoning || ''
          // Animate the typing effect
          animateTyping(fullText, reasoning)
        }
      }
    }
  )
}

const sendMessage = async () => {
  if (!inputMessage.value.trim() || isLoading.value) return

  trackEvent('widget_start')

  const userMessage = {
    role: 'user',
    content: inputMessage.value.trim(),
    timestamp: new Date()
  }

  messages.value.push(userMessage)
  const userMessageText = inputMessage.value.trim()
  inputMessage.value = ''
  isLoading.value = true

  // Reset textarea height
  if (textareaRef.value) {
    textareaRef.value.style.height = 'auto'
  }

  try {
    // Send message to backend to get AI response
    const response = await fetch('/api/widget/send_message', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fingerprint: userId.value,
        temp_chat_id: tempChatId.value,
        user_message: userMessageText,
        ai_model: props.ai_model,
        ai_provider: props.ai_provider
      })
    })

    const data = await response.json()

    if (!response.ok) {
      // Check if this is a registration limit error
      if (data.should_register) {
        trackEvent('widget_limit')
        isLoading.value = false
        messages.value.push({
          role: 'assistant',
          content: props.limit_message || data.error || 'Вы достигли лимита сообщений. Пожалуйста, зарегистрируйтесь, чтобы продолжить общение.',
          reasoning: '',
          timestamp: new Date()
        })
        return
      }
      throw new Error(data.error || 'Failed to send message')
    }

    tempChatId.value = data.temp_chat_id

    // The AI response will be received via ActionCable
    // isLoading will be set to false when the response arrives

  } catch (error) {
    console.error('Error sending message:', error)
    isLoading.value = false
    // Show error to user
    messages.value.push({
      role: 'assistant',
      content: 'Sorry, there was an error processing your message. Please try again.',
      reasoning: '',
      timestamp: new Date()
    })
  }
}

const trackRegistrationClick = (event) => {
  const link = event.target?.closest?.('a[href]')
  if (!link) return

  const url = new URL(link.href)
  if (url.origin === 'https://chat.aiacademy.me' && url.pathname === '/register') {
    trackEvent('widget_register_click')
  }
}

const loadChatHistory = async () => {
  if (!userId.value) return

  try {
    const response = await fetch(`/api/widget/load_chat?fingerprint=${userId.value}`)

    if (!response.ok) {
      throw new Error('Failed to load chat history')
    }

    const data = await response.json()
    tempChatId.value = data.temp_chat_id

    // Convert saved messages to our format
    if (data.messages && data.messages.length > 0) {
      messages.value = data.messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text,
        reasoning: msg.reasoning || '',
        timestamp: new Date()
      }))

      // Scroll to bottom after loading messages
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
        }
      })
    }
  } catch (error) {
    console.error('Error loading chat history:', error)
  }
}

const clearChat = async () => {
  // Stop any ongoing typing animation
  if (typingIntervalId.value) {
    clearInterval(typingIntervalId.value)
    typingIntervalId.value = null
  }

  // Close current chat on backend if it exists
  if (tempChatId.value) {
    try {
      await fetch('/api/widget/close_chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fingerprint: userId.value,
          temp_chat_id: tempChatId.value
        })
      })
    } catch (error) {
      console.error('Error closing chat:', error)
    }
  }

  // Clear local state
  messages.value = []
  tempChatId.value = null
  inputMessage.value = ''
  isLoading.value = false
  if (textareaRef.value) {
    textareaRef.value.style.height = 'auto'
  }
}

onMounted(async () => {
  // Get user fingerprint first
  try {
    userId.value = await getUserFingerprint()
    console.log('User ID:', userId.value)

    // A prefilled prompt always starts a new chat for this widget load.
    if (props.prompt === null) {
      await loadChatHistory()
    }
  } catch (error) {
    console.error('Failed to get user fingerprint:', error)
  } finally {
    isInitializing.value = false

    // Scroll to bottom after initialization is complete
    nextTick(() => {
      if (messagesContainer.value && messages.value.length > 0) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
      }
    })
  }

  // Focus on input when component mounts
  if (textareaRef.value) {
    textareaRef.value.focus()
  }
})

onUnmounted(() => {
  // Clean up typing animation
  if (typingIntervalId.value) {
    clearInterval(typingIntervalId.value)
    typingIntervalId.value = null
  }

  // Clean up ActionCable subscription
  if (subscription.value) {
    subscription.value.unsubscribe()
    subscription.value = null
  }
  if (cable.value) {
    cable.value.disconnect()
    cable.value = null
  }
})
</script>

<style scoped>
textarea {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  line-height: 1.5;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.animate-bounce {
  animation: bounce 1s ease-in-out infinite;
}
</style>
